import React, { useContext, useEffect, useState, useRef } from "react";
import hp from "../assets/imgs/home_profile.png";
import "owl.carousel/dist/assets/owl.carousel.css";
import "owl.carousel/dist/assets/owl.theme.default.css";
import { AppContext } from "../services/context";
import { baseApiUrl } from "../services/api";
// Imports pour les graphiques
import {
  BarChart, Bar,
  PieChart, Pie, Cell,
  LineChart, Line,
  CartesianGrid,
  XAxis, YAxis,
  Tooltip, Legend
} from 'recharts';
// Imports pour l'exportation
import { utils, write } from 'xlsx';
import { saveAs } from 'file-saver';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

const Home = () => {
  const authCtx = useContext(AppContext);
  const { user } = authCtx;
  const [userImg, setUserImg] = useState(hp);
  const [value, onChange] = useState(new Date());
  const [rdv, setRdv] = useState([]);
  const [stat, setStat] = useState({});
  const [groupCount, setGroupCount] = useState(0);
  const [userCount, setUserCount] = useState(0);
  const [clientsCount, setClientsCount] = useState(0);
  const [domaineCount, setDomaineCount] = useState(0);
  const [projetCount, setProjetCount] = useState(0);
  const [publicationCount, setPublicationCount] = useState(0);
  const [partenaireCount, setPartenaireCount] = useState(0);
  const [informationCount, setInformationCount] = useState(0);
  const [forumCount, setForumCount] = useState(0);
  const [submittedData, setSubmittedData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [monthlyStats, setMonthlyStats] = useState([]);
  const [activeTab, setActiveTab] = useState("bar");

  // Références pour les graphiques exportables
  const chartRef = useRef(null);
  const pieChartRef = useRef(null);
  const lineChartRef = useRef(null);
  const tableRef = useRef(null);

  // Maximum pour calculer les pourcentages des barres de progression
  const maxCount = 100;

  useEffect(() => {
    fetchAllData();
    generateMonthlyData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchUtilisateurs(),
        fetchProjets(),
        fetchDomaine(),
        fetchInformation(),
        fetchPublication(),
        fetchPartenaires(),
        fetchForums()
      ]);
    } catch (error) {
      console.error("Erreur lors du chargement des données:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUtilisateurs = async () => {
    try {
      const response = await baseApiUrl.get("/utilisateur");
      setUserCount(response.data.length);
      return response.data.length;
    } catch (error) {
      console.error("Erreur lors du chargement des utilisateurs", error);
      return 0;
    }
  };

  const fetchProjets = async () => {
    try {
      const response = await baseApiUrl.get("/projet/liste");
      setProjetCount(response.data.length);
      return response.data.length;
    } catch (error) {
      console.error("Erreur lors du chargement des projets", error);
      return 0;
    }
  };

  const fetchDomaine = async () => {
    try {
      const response = await baseApiUrl.get("/categorie/liste");
      setDomaineCount(response.data.length);
      return response.data.length;
    } catch (error) {
      console.error("Erreur lors du chargement des domaines", error);
      return 0;
    }
  };

  const fetchInformation = async () => {
    try {
      const response = await baseApiUrl.get("/information/liste");
      setInformationCount(response.data.length);
      return response.data.length;
    } catch (error) {
      console.error("Erreur lors du chargement des informations", error);
      return 0;
    }
  };

  const fetchPublication = async () => {
    try {
      const response = await baseApiUrl.get("/publication/liste");
      setPublicationCount(response.data.length);
      return response.data.length;
    } catch (error) {
      console.error("Erreur lors du chargement des publications", error);
      return 0;
    }
  };

  const fetchPartenaires = async () => {
    try {
      const response = await baseApiUrl.get("/partenaires/liste");
      setPartenaireCount(response.data.length);
      return response.data.length;
    } catch (error) {
      console.error("Erreur lors du chargement des partenaires", error);
      return 0;
    }
  };

  const fetchForums = async () => {
    try {
      const response = await baseApiUrl.get("/forum/liste");
      setForumCount(response.data.length);
      return response.data.length;
    } catch (error) {
      console.error("Erreur lors du chargement des forums", error);
      return 0;
    }
  };

  const generateMonthlyData = () => {
    const months = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Août", "Sep", "Oct", "Nov", "Déc"];
    const data = months.map((month) => {
      // Simuler des données mensuelles pour l'exemple
      return {
        name: month,
        Projets: Math.floor(Math.random() * 30) + 5,
        Publications: Math.floor(Math.random() * 20) + 2,
        Utilisateurs: Math.floor(Math.random() * 15) + 1,
        Forums: Math.floor(Math.random() * 10) + 1,
      };
    });
    setMonthlyStats(data);
  };

  // Calculer le pourcentage pour les barres de progression
  const calculatePercentage = (value) => {
    return (value / maxCount) * 100;
  };

  // Fonction pour exporter les données en Excel
  const exportToExcel = () => {
    try {
      // Créer un tableau de données pour Excel
      const data = [
        ['Catégorie', 'Nombre d\'entrées', 'Pourcentage'],
        ['Domaines', domaineCount, (calculatePercentage(domaineCount)).toFixed(1) + '%'],
        ['Projets', projetCount, (calculatePercentage(projetCount)).toFixed(1) + '%'],
        ['Utilisateurs', userCount, (calculatePercentage(userCount)).toFixed(1) + '%'],
        ['Forums', forumCount, (calculatePercentage(forumCount)).toFixed(1) + '%'],
        ['Actualités', informationCount, (calculatePercentage(informationCount)).toFixed(1) + '%'],
        ['Publications', publicationCount, (calculatePercentage(publicationCount)).toFixed(1) + '%'],
        ['Partenaires', partenaireCount, (calculatePercentage(partenaireCount)).toFixed(1) + '%']
      ];

      // Créer un classeur et une feuille avec les données
      const ws = utils.aoa_to_sheet(data);
      const wb = utils.book_new();
      utils.book_append_sheet(wb, ws, "Statistiques");

      // Générer le fichier Excel et le télécharger
      const excelBuffer = write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, "statistiques_plateforme.xlsx");
    } catch (error) {
      console.error("Erreur lors de l'exportation Excel:", error);
      alert("Une erreur est survenue lors de l'exportation en Excel.");
    }
  };

  // Fonction pour exporter les données en PDF
  const exportToPDF = async () => {
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');

      // Ajouter un titre
      pdf.setFontSize(18);
      pdf.text("Statistiques de la Plateforme", 105, 15, { align: 'center' });

      // Exporter le tableau
      if (tableRef.current) {
        const tableCanvas = await html2canvas(tableRef.current);
        const tableImgData = tableCanvas.toDataURL('image/png');
        pdf.addImage(tableImgData, 'PNG', 10, 25, 190, 100);
      }

      // Exporter le graphique actif
      let chartElement;
      if (activeTab === 'bar') chartElement = chartRef.current;
      else if (activeTab === 'pie') chartElement = pieChartRef.current;
      else if (activeTab === 'line') chartElement = lineChartRef.current;

      if (chartElement) {
        const chartCanvas = await html2canvas(chartElement);
        const chartImgData = chartCanvas.toDataURL('image/png');
        pdf.addImage(chartImgData, 'PNG', 10, 130, 190, 100);
      }

      // Télécharger le PDF
      pdf.save("statistiques_plateforme.pdf");
    } catch (error) {
      console.error("Erreur lors de l'exportation PDF:", error);
      alert("Une erreur est survenue lors de l'exportation en PDF.");
    }
  };

  // Fonction pour exporter en image
  const exportToImage = async () => {
    try {
      let chartElement;
      if (activeTab === 'bar') chartElement = chartRef.current;
      else if (activeTab === 'pie') chartElement = pieChartRef.current;
      else if (activeTab === 'line') chartElement = lineChartRef.current;

      if (chartElement) {
        const canvas = await html2canvas(chartElement);
        const imgData = canvas.toDataURL('image/png');

        // Créer un lien pour télécharger l'image
        const link = document.createElement('a');
        link.href = imgData;
        link.download = `statistiques_${activeTab}.png`;
        link.click();
      } else {
        alert("Aucun graphique n'est disponible pour l'exportation.");
      }
    } catch (error) {
      console.error("Erreur lors de l'exportation d'image:", error);
      alert("Une erreur est survenue lors de l'exportation en image.");
    }
  };

  // Données pour le graphique en camembert
  const pieData = [
    { name: 'Domaines', value: domaineCount, fill: '#0d6efd' },
    { name: 'Projets', value: projetCount, fill: '#20c997' },
    { name: 'Utilisateurs', value: userCount, fill: '#dc3545' },
    { name: 'Publications', value: publicationCount, fill: '#fd7e14' },
    { name: 'Actualités', value: informationCount, fill: '#6f42c1' },
    { name: 'Partenaires', value: partenaireCount, fill: '#0dcaf0' },
    { name: 'Forums', value: forumCount, fill: '#ffc107' },
  ];

  // Données pour le graphique à barres
  const barData = [
    { name: 'Domaines', count: domaineCount, fill: '#0d6efd' },
    { name: 'Projets', count: projetCount, fill: '#20c997' },
    { name: 'Utilisateurs', count: userCount, fill: '#dc3545' },
    { name: 'Forums', count: forumCount, fill: '#ffc107' },
    { name: 'Actualités', count: informationCount, fill: '#0dcaf0' },
    { name: 'Publications', count: publicationCount, fill: '#198754' },
    { name: 'Partenaires', count: partenaireCount, fill: '#6f42c1' }
  ];

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="content container-fluid pb-0">
          <div className="text-center my-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Chargement...</span>
            </div>
            <p className="mt-3">Chargement des données...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="page-wrapper">
        <div className="content container-fluid pb-0">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <h3 className="page-title">Bienvenue !</h3>
                <ul className="breadcrumb">
                  <li className="breadcrumb-item active">Tableau de bord</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Première rangée de statistiques */}
          <div className="row">
            <div className="col-xl-3 col-sm-6 col-12">
              <div className="card">
                <div className="card-body">
                  <div className="dash-widget-header">
                    <span className="dash-widget-icon text-primary border-primary">
                      <i className="fe fe-users" />
                    </span>
                    <div className="dash-count">
                      <h3>{domaineCount}</h3>
                    </div>
                  </div>
                  <div className="dash-widget-info">
                    <h6 className="text-muted">Domaines</h6>
                    <div className="progress progress-sm">
                      <div
                        className="progress-bar bg-primary"
                        style={{ width: `${calculatePercentage(domaineCount)}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xl-3 col-sm-6 col-12">
              <div className="card">
                <div className="card-body">
                  <div className="dash-widget-header">
                    <span className="dash-widget-icon text-success">
                      <i className="fe fe-folder" />
                    </span>
                    <div className="dash-count">
                      <h3>{projetCount}</h3>
                    </div>
                  </div>
                  <div className="dash-widget-info">
                    <h6 className="text-muted">Projets</h6>
                    <div className="progress progress-sm">
                      <div
                        className="progress-bar bg-success"
                        style={{ width: `${calculatePercentage(projetCount)}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xl-3 col-sm-6 col-12">
              <div className="card">
                <div className="card-body">
                  <div className="dash-widget-header">
                    <span className="dash-widget-icon text-danger border-danger">
                      <i className="fe fe-user" />
                    </span>
                    <div className="dash-count">
                      <h3>{userCount}</h3>
                    </div>
                  </div>
                  <div className="dash-widget-info">
                    <h6 className="text-muted">Utilisateurs</h6>
                    <div className="progress progress-sm">
                      <div
                        className="progress-bar bg-danger"
                        style={{ width: `${calculatePercentage(userCount)}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xl-3 col-sm-6 col-12">
              <div className="card">
                <div className="card-body">
                  <div className="dash-widget-header">
                    <span className="dash-widget-icon text-warning border-warning">
                      <i className="fa fa-comment-o" />
                    </span>
                    <div className="dash-count">
                      <h3>{forumCount}</h3>
                    </div>
                  </div>
                  <div className="dash-widget-info">
                    <h6 className="text-muted">Forums</h6>
                    <div className="progress progress-sm">
                      <div
                        className="progress-bar bg-warning"
                        style={{ width: `${calculatePercentage(forumCount)}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Deuxième rangée de statistiques */}
          <div className="row">
            <div className="col-xl-3 col-sm-6 col-12">
              <div className="card">
                <div className="card-body">
                  <div className="dash-widget-header">
                    <span className="dash-widget-icon text-info border-info">
                      <i className="fe fe-file-text" />
                    </span>
                    <div className="dash-count">
                      <h3>{informationCount}</h3>
                    </div>
                  </div>
                  <div className="dash-widget-info">
                    <h6 className="text-muted">Actualités</h6>
                    <div className="progress progress-sm">
                      <div
                        className="progress-bar bg-info"
                        style={{ width: `${calculatePercentage(informationCount)}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xl-3 col-sm-6 col-12">
              <div className="card">
                <div className="card-body">
                  <div className="dash-widget-header">
                    <span className="dash-widget-icon text-success border-success">
                      <i className="fe fe-file" />
                    </span>
                    <div className="dash-count">
                      <h3>{publicationCount}</h3>
                    </div>
                  </div>
                  <div className="dash-widget-info">
                    <h6 className="text-muted">Publications</h6>
                    <div className="progress progress-sm">
                      <div
                        className="progress-bar bg-success"
                        style={{ width: `${calculatePercentage(publicationCount)}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xl-3 col-sm-6 col-12">
              <div className="card">
                <div className="card-body">
                  <div className="dash-widget-header">
                    <span className="dash-widget-icon text-primary border-primary">
                      <i className="fe fe-users" />
                    </span>
                    <div className="dash-count">
                      <h3>{partenaireCount}</h3>
                    </div>
                  </div>
                  <div className="dash-widget-info">
                    <h6 className="text-muted">Partenaires</h6>
                    <div className="progress progress-sm">
                      <div
                        className="progress-bar bg-primary"
                        style={{ width: `${calculatePercentage(partenaireCount)}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Boutons d'exportation */}
            <div className="col-xl-3 col-sm-6 col-12">
              <div className="card">
                <div className="card-body">
                  <h5 className="card-title">Exporter les données</h5>
                  <div className="d-grid gap-2">
                    <button
                      className="btn btn-sm btn-success mb-2"
                      onClick={exportToExcel}
                    >
                      <i className="fa fa-file-excel-o me-1"></i> Exporter en Excel
                    </button>
                    <button
                      className="btn btn-sm btn-danger mb-2"
                      onClick={exportToPDF}
                    >
                      <i className="fa fa-file-pdf-o me-1"></i> Exporter en PDF
                    </button>
                    <button
                      className="btn btn-sm btn-info"
                      onClick={exportToImage}
                    >
                      <i className="fa fa-file-image-o me-1"></i> Exporter en image
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section graphiques */}
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h4 className="card-title">Statistiques de la plateforme</h4>
                  <div className="card-tools">
                    <ul className="nav nav-tabs" role="tablist">
                      <li className="nav-item">
                        <button
                          className={`nav-link ${activeTab === 'bar' ? 'active' : ''}`}
                          onClick={() => setActiveTab('bar')}
                        >
                          Histogramme
                        </button>
                      </li>
                      <li className="nav-item">
                        <button
                          className={`nav-link ${activeTab === 'pie' ? 'active' : ''}`}
                          onClick={() => setActiveTab('pie')}
                        >
                          Circulaire
                        </button>
                      </li>
                      <li className="nav-item">
                        <button
                          className={`nav-link ${activeTab === 'line' ? 'active' : ''}`}
                          onClick={() => setActiveTab('line')}
                        >
                          Ligne
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="card-body">
                  <div className="chart-container">
                    {activeTab === 'bar' && (
                      <div ref={chartRef}>
                        <BarChart
                          width={800}
                          height={400}
                          data={barData}
                          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="count" name="Nombre" fill="#8884d8" />
                        </BarChart>
                      </div>
                    )}

                    {activeTab === 'pie' && (
                      <div ref={pieChartRef}>
                        <PieChart width={800} height={400}>
                          <Pie
                            data={pieData}
                            cx={400}
                            cy={200}
                            labelLine={true}
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            outerRadius={150}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {pieData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.fill} />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </div>
                    )}

                    {activeTab === 'line' && (
                      <div ref={lineChartRef}>
                        <LineChart
                          width={800}
                          height={400}
                          data={monthlyStats}
                          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Line type="monotone" dataKey="Projets" stroke="#20c997" activeDot={{ r: 8 }} />
                          <Line type="monotone" dataKey="Publications" stroke="#fd7e14" />
                          <Line type="monotone" dataKey="Utilisateurs" stroke="#dc3545" />
                          <Line type="monotone" dataKey="Forums" stroke="#ffc107" />
                        </LineChart>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tableau des données */}
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h4 className="card-title">Résumé des statistiques</h4>
                </div>
                <div className="card-body">
                  <div className="table-responsive" ref={tableRef}>
                    <table className="table table-bordered table-striped">
                      <thead>
                        <tr>
                          <th>Catégorie</th>
                          <th>Nombre d'entrées</th>
                          <th>Pourcentage</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Domaines</td>
                          <td>{domaineCount}</td>
                          <td>{(calculatePercentage(domaineCount)).toFixed(1)}%</td>
                        </tr>
                        <tr>
                          <td>Projets</td>
                          <td>{projetCount}</td>
                          <td>{(calculatePercentage(projetCount)).toFixed(1)}%</td>
                        </tr>
                        <tr>
                          <td>Utilisateurs</td>
                          <td>{userCount}</td>
                          <td>{(calculatePercentage(userCount)).toFixed(1)}%</td>
                        </tr>
                        <tr>
                          <td>Forums</td>
                          <td>{forumCount}</td>
                          <td>{(calculatePercentage(forumCount)).toFixed(1)}%</td>
                        </tr>
                        <tr>
                          <td>Actualités</td>
                          <td>{informationCount}</td>
                          <td>{(calculatePercentage(informationCount)).toFixed(1)}%</td>
                        </tr>
                        <tr>
                          <td>Publications</td>
                          <td>{publicationCount}</td>
                          <td>{(calculatePercentage(publicationCount)).toFixed(1)}%</td>
                        </tr>
                        <tr>
                          <td>Partenaires</td>
                          <td>{partenaireCount}</td>
                          <td>{(calculatePercentage(partenaireCount)).toFixed(1)}%</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Home;
